import { fillEmptyCells, removeEmptyP } from "functions/TemplateEditor";
import { parseComplexStyleProperty } from "./templateGenerator";

export const changeSectionBgColor = (htmlContent, color) => {
  try {
    const domParser = new DOMParser();
    const doc = domParser.parseFromString(htmlContent, "text/html");
    const node = doc.body.firstChild;
    const element = node.firstChild.parentElement;
    if (element) {
      element.style.backgroundColor = `${color}`;
      return element.outerHTML;
    } else {
      return htmlContent;
    }
  } catch (error) {
    return htmlContent;
  }
};

export const rgb2hex = (rgb) => {
  if (rgb) {
    const rgbValues = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (rgbValues) {
      return `#${rgbValues
        .slice(1)
        .map((n) => parseInt(n, 10).toString(16).padStart(2, "0"))
        .join("")}`;
    } else {
      return `#FFFFFF`;
    }
  } else {
    return "#FFFFFF";
  }
};

function getSelectionParentElement() {
  var parentEl = null,
    sel;
  if (window.getSelection) {
    sel = window.getSelection();
    if (sel.rangeCount) {
      parentEl = sel.getRangeAt(0).commonAncestorContainer;
      if (parentEl.nodeType != 1) {
        parentEl = parentEl.parentNode;
      }
    }
  } else if ((sel = document.selection) && sel.type != "Control") {
    parentEl = sel.createRange().parentElement();
  }
  return parentEl;
}

export const pasteHtmlAtCaret = (param) => {
  var range;
  if (window.getSelection) {
    // IE9 and non-IE
    let windowSelection = window.getSelection();
    if (windowSelection.getRangeAt && windowSelection.rangeCount) {
      range = windowSelection.getRangeAt(0);
      range.deleteContents();

      // Range.createContextualFragment() would be useful here but is
      // non-standard and not supported in all browsers (IE9, for one)
      var el = document.createElement("div");
      el.innerHTML = `<params contenteditable="false" suppressContentEditableWarning="true">${param}</params> `;
      var frag = document.createDocumentFragment(),
        node,
        lastNode;
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);

      // Preserve the selection
      if (lastNode) {
        range = range.cloneRange();
        range.setStartAfter(lastNode);
        range.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(range);
      }
    }
  } else if (document.selection && document.selection.type != "Control") {
    // IE < 9
    document.selection.createRange().pasteHTML(html);
  }
};

export const calculateSectionSize = (sectionRef, width, height) => {
  try {
    let size = { width: parseInt(width), height: parseInt(height) };
    if (sectionRef) {
      const clientWidth = sectionRef.current.clientWidth;
      const clientHeight = sectionRef.current.clientHeight;
      if (clientWidth > 0 && clientWidth <= width) {
        size.width = clientWidth;
      }

      if (clientHeight > 0 && clientHeight <= height) {
        size.height = clientHeight + 10;
      }
      return size;
    } else {
      return size;
    }
  } catch (error) {
    return { width, height };
  }
};

export const isAbleToAddParam = (sectionName) => {
  try {
    const element = document.querySelector(
      `.editing[sectionname="${sectionName}"][defaultsection="true"]`
    );
    if (element) {
      const selectionElement = getSelectionParentElement();
      return element.contains(selectionElement);
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * @description handle width and height change of a section
 * @param {string} id - section id
 * @param {number} width - width value in px
 * @param {number} height - height value in px
 */
export const changeSize = (id, width, height) => {
  try {
    const section = document.getElementById(id);
    if (section) {
      section.style.width = `${width}px`;
      section.style.height = `${height}px`;
    }
  } catch (error) {
    console.error("Section change size failed : ", error);
  }
};

/**
 * @description handle x and y transform of a section
 * @param {string} id - section id
 * @param {number} x - transform value in x
 * @param {number} y - transform value in y
 */
export const changePosition = (id, x, y) => {
  try {
    const section = document.getElementById(id);
    if (section) {
      let position = parseComplexStyleProperty(section.style.transform);
      position = {
        x: position.x + x,
        y: position.y + y,
      };
      section.style.transform = `translate(${position.x}px, ${position.y}px)`;
      section.setAttribute("data-x", position.x);
      section.setAttribute("data-y", position.y);
    }
  } catch (error) {
    console.error("Section change position failed : ", error);
  }
};

/**
 * @description lock all template sections
 * @param {boolean} isLocked - lock status
 * returns {Array} locked section ids
 */
export const lockAllSection = (isLocked) => {
  try {
    const sections = document.querySelectorAll(".usc-section");

    sections?.forEach((section) => {
      if (isLocked) {
        section?.setAttribute("locked", "true");
      } else {
        section?.removeAttribute("locked");
      }
    });
  } catch (error) {
    console.error("lockAllSection failed : ", error);
  }
};

/**
 * @description - Get section lock status
 * @sectionId - current section id
 */
export const getLockStatus = (sectionId) => {
  try {
    if (sectionId) {
      const element = document.getElementById(`${sectionId}`);
      if (element) {
        return Boolean(element.getAttribute("locked"));
      }
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * @description - Check whether the section can drag or not
 * @param - current section id
 */
export const isEnableDrag = (sectionId) => {
  try {
    const element = document.getElementById(sectionId);
    if (element) {
      const isLocked = Boolean(element.getAttribute("locked"));
      const isEditing = element.classList.contains("editing");
      return !isLocked && !isEditing;
    } else {
      return true;
    }
  } catch (error) {
    return true;
  }
};

/**
 * @description - Calculate maximum width and height of a section
 * @param - current section id
 */
export const getMaxSizeForSection = (section) => {
  let maxSize = {
    width: section?.width,
    height: section?.height,
  };
  try {
    const pageSize = document
      .getElementById("divUSCPageContent")
      .getBoundingClientRect();
    if (pageSize) {
      maxSize.width = Math.round(pageSize.width);
      maxSize.height = Math.round(pageSize.height);
    }
    return maxSize;
  } catch (error) {
    return maxSize;
  }
};

export const getSectionData = (sectionName) => {
  let sectionData = {
    id: "",
    name: sectionName,
    rules: [],
    defaultRule: "input1.Default",
    repeatingAdapterId: 0,
    locked: false,
    left: 0,
    top: 0,
    color: "#ffffff",
    properties: {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
      x: 0,
      y: 0,
    },
    selectedRule: "input1.Default",
    repeatable: false,
  };
  try {
    if (sectionName) {
      /**
       * Select all the section element related to the section name which are hidden ones and visible one
       */
      const items = document.querySelectorAll(
        `div[sectionname="${sectionName}"]`
      );

      // iterate all the section elements to identify the default section and others
      if (items && items.length > 0) {
        items.forEach((item) => {
          const rule = item.getAttribute("rule");
          const isDeleted = Boolean(item?.getAttribute("deleted"));

          // get measurements for the selected section
          // check the default rule
          if (rule) {
            const isDefault = Boolean(item.getAttribute("defaultsection"));
            if (isDefault) {
              sectionData.defaultRule = rule;
              sectionData.id = item.getAttribute("id");
              sectionData.selectedRule = rule;
            }
            sectionData.rules = [...sectionData.rules, { rule, isDeleted }];
          }
          const isActive =
            !item?.hasAttribute("hidden") ||
            (item?.hasAttribute("hidden") &&
              item?.getAttribute("hidden") == "false");
          if (isActive) {
            sectionData.selectedRule = rule;
          }
        });
      }

      const sectionElement = document.getElementById(sectionData.id);

      if (sectionElement) {
        /**
         * Extract section element properties
         */
        sectionData.color = rgb2hex(sectionElement.style.backgroundColor);
        sectionData.locked = Boolean(sectionElement.getAttribute("locked"));

        const properties = sectionElement.getBoundingClientRect();
        const left = sectionElement?.style.left;
        const top = sectionElement?.style.top;
        sectionData.properties = Object.assign(properties, {
          x: parseInt(left, 10),
          y: parseInt(top, 10),
        });

        // read the repeating adapter id of the section element
        const repeatingAdapterId = parseInt(
          sectionElement.getAttribute("repeatingadapterid") ?? 0
        );
        sectionData.repeatingAdapterId = repeatingAdapterId;

        // get the repeatable table element if exist
        const table = sectionElement.querySelector("table[repeatable]");
        if (repeatingAdapterId || table) {
          sectionData.repeatable = true;
        }
      }

      return sectionData;
    } else {
      return sectionData;
    }
  } catch (error) {
    console.error("getSectionData error ==> ", error);
    return sectionData;
  }
};

export const getSectionDataByRule = (sectionName, sectionRule) => {
  let sectionData = {
    id: "",
    name: sectionName,
    defaultRule: "input1.Default",
    repeatingAdapterId: 0,
    locked: false,
    left: 0,
    top: 0,
    color: "#ffffff",
    properties: {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
      x: 0,
      y: 0,
    },
    selectedRule: "input1.Default",
    repeatable: false,
  };
  try {
    if (sectionName && sectionRule) {
      /**
       * Select all the section element related to the section name which are hidden ones and visible one
       */
      const sectionElement = document.querySelector(
        `div[sectionname='${sectionName}'][rule='${sectionRule}']`
      );

      // get measurements for the selected section
      // check the default rule

      sectionData.id = sectionElement.getAttribute("id");
      sectionData.selectedRule = sectionRule;

      if (sectionElement) {
        /**
         * Extract section element properties
         */
        sectionData.color = rgb2hex(sectionElement.style.backgroundColor);
        sectionData.locked = Boolean(sectionElement.getAttribute("locked"));

        const properties = sectionElement.getBoundingClientRect();
        const left = sectionElement?.style.left;
        const top = sectionElement?.style.top;
        sectionData.properties = Object.assign(properties, {
          x: parseInt(left, 10),
          y: parseInt(top, 10),
        });

        // read the repeating adapter id of the section element
        const repeatingAdapterId = parseInt(
          sectionElement.getAttribute("repeatingadapterid") ?? 0
        );
        sectionData.repeatingAdapterId = repeatingAdapterId;

        // get the repeatable table element if exist
        const table = sectionElement.querySelector("table[repeatable]");
        if (table) {
          sectionData.repeatable = true;
        }
      }

      return sectionData;
    } else {
      return sectionData;
    }
  } catch (error) {
    console.error("getSectionData error ==> ", error);
    return sectionData;
  }
};

export const getSectionRuleContent = (sectionRule) => {
  try {
    const domParser = new DOMParser();
    const sanitizedContent = removeEmptyP(sectionRule.content);
    const node = domParser.parseFromString(sanitizedContent, "text/html").body
      .firstChild;

    if (node) {
      if (node.firstChild) {
        const tables = node.firstChild.parentElement?.querySelectorAll("table");
        tables?.forEach((table, index) => {
          if (table) {
            // handling repeating parameters
            if (sectionRule.isRepeating) {
              const replaceableElement = table.querySelector(
                "tr[id=repeatingcontent]"
              );
              if (replaceableElement) {
                table.setAttribute("repeatable", "true");
                table.setAttribute("id", "repeatableTable");
                table.setAttribute(
                  "repeatingadapterid",
                  `${sectionRule.repeatingAdapterId}`
                );

                const tempElement = document.createElement("table");
                tempElement.insertAdjacentHTML(
                  "beforeend",
                  sectionRule.repeatingContent ?? ""
                );
                const repeatableContent = tempElement.querySelector("tr");
                if (repeatableContent) {
                  repeatableContent.setAttribute(
                    "repeatingadapterid",
                    `${sectionRule.repeatingAdapterId}`
                  );
                  const params = repeatableContent.querySelectorAll("params");
                  params.forEach((param, index) => {
                    param.setAttribute("isrepeating", "true");
                  });
                  replaceableElement.replaceWith(repeatableContent);
                }
              }
            }
          }
        });
        const paramsElements = node.parentElement.querySelectorAll("params");
        paramsElements.forEach((element) => {
          if (!element.hasAttribute("contenteditable")) {
            element.setAttribute("contenteditable", "false");
          }
          if (!element.hasAttribute("suppressContentEditableWarning")) {
            element.setAttribute("suppressContentEditableWarning", "true");
          }
        });
        return node.firstChild.parentElement.outerHTML;
      } else {
        return node.parentElement.innerHTML;
      }
    } else {
      return sectionRule.content;
    }
  } catch (error) {
    console.error("getSectionRuleContent ==> ", error);
    return sectionRule.content;
  }
};

export const getPurifiedTextContent = (content) => {
  try {
    const domParser = new DOMParser();
    const sanitizedContent = removeEmptyP(content);
    const node = domParser.parseFromString(sanitizedContent, "text/html").body
      .firstChild;

    if (node) {
      const paramsElements = node.parentElement.querySelectorAll("params");
      paramsElements.forEach((element) => {
        if (!element.hasAttribute("contenteditable")) {
          element.setAttribute("contenteditable", "false");
        }
        if (!element.hasAttribute("suppressContentEditableWarning")) {
          element.setAttribute("suppressContentEditableWarning", "true");
        }
      });
      return node.parentElement.innerHTML;
    } else {
      return content;
    }
  } catch (error) {
    console.error("getSectionRuleContent ==> ", error);
    return content;
  }
};

/**
 * @description - update table to show border color
 * @param {any} htmlContent section editor html content
 */
export const updateTableCellBorderStyle = (htmlContent) => {
  // Create a temporary element to parse the HTML content
  try {
    var tempElement = document.createElement("div");
    tempElement.innerHTML = htmlContent;

    var tdElements = tempElement.querySelectorAll("td");
    if (tdElements) {
      tdElements.forEach((tdElement) => {
        const borderStyle = tdElement.style.border;
        if (!borderStyle) {
          const borderColor = tdElement.style.borderColor;
          if (borderColor && borderColor !== "initial") {
            tdElement.style.border = "1px solid " + borderColor;
          }
        }
      });
    }
    return tempElement.innerHTML;
  } catch (error) {
    return htmlContent;
  }
};

export const generateSectionContents = (oldSectionRule, content) => {
  try {
    let repeatingAdapterId = 0,
      repeatingContent = "";

    const modifiedContent = updateTableCellBorderStyle(content);
    const purifiedContent = fillEmptyCells(modifiedContent);
    const domParser = new DOMParser();
    const parentNode = domParser.parseFromString(purifiedContent, "text/html")
      .body.firstChild;
    if (
      parentNode.nodeName != "DIV" &&
      !parentNode.parentElement.hasAttribute("style")
    ) {
      const oldNode = domParser.parseFromString(
        oldSectionRule.content,
        "text/html"
      ).body.firstChild;
      const oldParentNode = oldNode.firstChild.parentNode;
      oldParentNode.replaceChildren();
      oldParentNode.innerHTML = modifiedContent;
      const repeatingRow = oldParentNode.parentElement.querySelector(
        "tr[id=repeatingcontent]"
      );
      if (repeatingRow) {
        repeatingAdapterId = repeatingRow.getAttribute("repeatingadapterid");
        repeatingRow.removeAttribute("repeatingadapterid");
        repeatingContent = _.cloneDeep(repeatingRow.outerHTML);
        repeatingRow.replaceChildren();
      }

      return {
        content: oldParentNode.parentElement.innerHTML,
        repeatingContent,
        isRepeating: repeatingRow != null,
        repeatingAdapterId: repeatingAdapterId,
      };
    } else {
      const repeatingRow = parentNode.querySelector("tr[id=repeatingcontent]");
      if (repeatingRow) {
        repeatingAdapterId = repeatingRow.getAttribute("repeatingadapterid");
        repeatingRow.removeAttribute("repeatingadapterid");
        repeatingContent = _.cloneDeep(repeatingRow.outerHTML);
        repeatingRow.replaceChildren();
      }

      return {
        content: parentNode.firstChild.parentElement.outerHTML,
        repeatingContent: repeatingContent,
        isRepeating: repeatingRow != null,
        repeatingAdapterId: repeatingAdapterId,
      };
    }
  } catch (error) {
    return {
      content: oldSectionRule.content,
      repeatingContent: oldSectionRule.repeatingContent,
      isRepeating: oldSectionRule.isRepeating,
      repeatingAdapterId: oldSectionRule.repeatingAdapterId,
    };
  }
};

const getNearestAncestor = (htmlElementNode, ancestor) => {
  try {
    while (htmlElementNode) {
      htmlElementNode = htmlElementNode.parentNode;
      if (htmlElementNode.tagName.toLowerCase() === ancestor) {
        return htmlElementNode;
      }
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const insertRow = (editor, isAbove = false) => {
  try {
    const selectedCell = editor.selection.current();
    const table = getNearestAncestor(selectedCell, "tbody");
    const selectedRow = getNearestAncestor(selectedCell, "tr");
    const columnCount = selectedRow.querySelectorAll("td").length;
    if (table) {
      const newIndex = isAbove
        ? selectedRow.rowIndex
        : selectedRow.rowIndex + 1;
      var row = table.insertRow(newIndex);
      for (let i = 0; i < columnCount; i++) {
        var cell = row.insertCell(i);
        cell.innerHTML = "&nbsp;";
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export const updateSectionRuleProperties = (sectionRule, properties) => {
  try {
    const domParser = new DOMParser();
    const node = domParser.parseFromString(sectionRule.content, "text/html")
      .body.firstChild;
    const parentElement = node.firstChild.parentElement;
    parentElement.style.width = `${Math.round(properties.width)}px`;
    parentElement.style.height = `${Math.round(properties.height)}px`;
    parentElement.style.transform =
      "translate(" + properties.dx + "px, " + properties.dy + "px)";
    return {
      ...sectionRule,
      content: parentElement.outerHTML,
    };
  } catch (error) {
    return sectionRule;
  }
};

/**
 * @description - Setup original section root div position to the newly edited section content
 * @param {string | undefined} oldContent
 * @param {string} newContent
 */
export const positioningWithRootDiv = (oldContent, newContent) => {
  try {
    const domParser = new DOMParser();
    const oldNode = domParser.parseFromString(oldContent, "text/html").body
      .firstChild;
    const oldElement = oldNode.firstChild.parentElement;

    const adjustedContent = adjustContent(newContent);
    const newNode = domParser.parseFromString(
      adjustedContent.content,
      "text/html"
    ).body.firstChild;
    if (newNode.nodeName == "TABLE") {
      const newElement = newNode;
      oldElement.style.backgroundColor = newElement.style.backgroundColor;
      oldElement.style.width = newElement.style.width;
      oldElement.style.height = newElement.style.height;
      oldElement.style.border = newElement.style.border;
      oldElement.innerHTML = newElement.innerHTML;
      return oldElement.outerHTML;
    } else {
      const newElement = newNode.firstChild.parentElement;
      oldElement.style.backgroundColor = newElement.style.backgroundColor;
      oldElement.style.width = newElement.style.width;
      oldElement.style.height = newElement.style.height;
      oldElement.style.border = newElement.style.border;
      oldElement.innerHTML = newElement.innerHTML;
      return oldElement.outerHTML;
    }
  } catch (error) {
    console.error("positioningWithRootDiv error : ", error);
    return newContent;
  }
};

/**
 * @description - Adjust content those add to the outside of the parent div
 * @param {string} content
 */
export const adjustContent = (content) => {
  try {
    const domParser = new DOMParser();
    const body = domParser.parseFromString(content, "text/html").body;
    const lastDivElement = body.querySelector("div:last-of-type");
    const lastImgElement = lastDivElement?.querySelector("img");
    const lastImgWithSrc = lastDivElement?.querySelector("img[src]");
    if (body.children.length > 1 && !(lastImgElement && !lastImgWithSrc)) {
      const parentDiv = body.querySelector("div");
      if (parentDiv) {
        body.removeChild(parentDiv);
        for (const childNode of body.childNodes) {
          if (childNode.nodeType != Node.TEXT_NODE) {
            if (childNode?.querySelector("img")) {
              parentDiv.appendChild(childNode.firstChild);
            } else {
              parentDiv.appendChild(childNode);
            }
          }
        }
      }
      return { content: parentDiv.outerHTML, isModified: true };
    } else {
      return { content, isModified: false };
    }
  } catch (error) {
    console.error("adjustContent error : ", error);
    return { content, isModified: false };
  }
};

/**
 * @description - Preapre content for the SMS section
 * @param {any} oldSectionRule
 * @param {string} content
 */
export const generateSMSContent = (oldSectionRule, content) => {
  try {
    const domParser = new DOMParser();
    const oldNode = domParser.parseFromString(
      oldSectionRule.content,
      "text/html"
    ).body.firstChild;
    const parentDiv = oldNode.parentElement.querySelector("div");
    parentDiv.replaceChildren();
    parentDiv.innerHTML = content;

    return {
      content: parentDiv.outerHTML,
      repeatingContent: "",
      isRepeating: false,
      repeatingAdapterId: 0,
    };
  } catch (error) {
    return {
      content: oldSectionRule.content,
      repeatingContent: oldSectionRule.repeatingContent,
      isRepeating: oldSectionRule.isRepeating,
      repeatingAdapterId: oldSectionRule.repeatingAdapterId,
    };
  }
};
